import React, { useState } from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { ProductGallery } from './ProductGallery';

const ProductDetail = ({ product, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  if (!product) return null;

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast({
      title: 'Added to cart',
      description: `${quantity}x ${product.name} added to your cart.`,
    });
    onClose();
  };

  return (
    <div data-testid="product-detail-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="h-6 w-6 text-[#001B2A]" />
        </button>

        <div className="grid md:grid-cols-2 gap-8 p-6 sm:p-8">
          <ProductGallery product={product} />

          {/* Product Info */}
          <div className="space-y-6">
            <div className="flex gap-2">
              {product.isNew && (
                <span className="bg-[#7B1E2B] text-white text-xs font-semibold px-3 py-1 rounded-full">
                  NEW ARRIVAL
                </span>
              )}
              {product.isBestSeller && (
                <span className="bg-[#E6D7B3] text-[#001B2A] text-xs font-semibold px-3 py-1 rounded-full">
                  BEST SELLER
                </span>
              )}
            </div>

            <div>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-[#001B2A] mb-2">
                {product.name}
              </h2>
              <p className="text-3xl sm:text-4xl font-bold text-[#7B1E2B]">
                ${product.price.toFixed(2)}
              </p>
            </div>

            <p className="text-gray-600 leading-relaxed">{product.description}</p>

            <div className="space-y-3 border-t border-b border-gray-200 py-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Material:</span>
                <span className="text-sm text-gray-900">{product.material}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Colors:</span>
                <span className="text-sm text-gray-900">{product.colors.join(', ')}</span>
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">Quantity:</label>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  data-testid="modal-qty-decrease"
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="text-lg font-semibold w-12 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity((prev) => prev + 1)}
                  data-testid="modal-qty-increase"
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <button
              onClick={handleAddToCart}
              className="w-full flex items-center justify-center gap-3 bg-[#001B2A] text-white py-4 rounded-md hover:bg-[#7B1E2B] transition-all duration-300 shadow-lg hover:shadow-xl font-semibold text-lg"
            >
              <ShoppingBag className="h-5 w-5" />
              ADD TO CART - ${(product.price * quantity).toFixed(2)}
            </button>

            <div className="space-y-2 text-sm text-gray-600">
              <p>✓ Free shipping on orders over $100</p>
              <p>✓ 30-day return policy</p>
              <p>✓ Handcrafted with premium materials</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
